import React from 'react'
import { RadioGroup } from '@headlessui/react'
import clsx from 'clsx'
import { CheckCircleIcon } from '@heroicons/react/20/solid'

// Updated User interface to match JiraUser from jira-types.ts
interface User {
  accountId: string  // Changed from id to accountId
  displayName: string // Changed from name to displayName
  avatarUrl?: string // Changed from avatar to avatarUrl
  role?: string
}

interface Board {
  id: string
  name: string
}

interface Project {
  id: string
  key: string
  name: string
  boards: Board[]
  users: User[]
}

interface ProjectUserSelectorProps {
  onChangeUser: (userId: string) => void
  project: Project
  user: User | null
}

const ProjectUserSelector: React.FC<ProjectUserSelectorProps> = ({ onChangeUser, project, user }) => {

  const handleChange = (userId: string) => {
    onChangeUser(userId)
  }

  // Ensure users array exists before sorting
  const users = project.users || []
  if (users.length > 0) {
    // Sort using accountId instead of id
    users.sort((a, b) => (a.accountId > b.accountId ? -1 : 1))
  }

  return (
    <div className='mx-auto max-w-7xl pb-6'>
      <RadioGroup
        value={user?.accountId} // Changed from id to accountId
        onChange={handleChange}
        className='grid grid-cols-2 gap-6 md:grid-cols-3 lg:grid-cols-4'
      >
        {users.map(user => (
          <RadioGroup.Option
            key={`user-${user.accountId}`} // Changed from id to accountId
            value={user.accountId} // Changed from id to accountId
            as='div'
            className={({ active, checked }) =>
              clsx(
                // base layout
                'relative flex cursor-pointer rounded-lg px-5 py-2 shadow-md focus:outline-none',
                // checked / unchecked backgrounds
                checked ? 'bg-sky-900 bg-opacity-75 text-white' : 'bg-white',
                // active state when not checked
                active && !checked && 'shadow-lg',
                // unified hover interaction
                'transition-all duration-150 hover:shadow-lg hover:ring-2 hover:ring-indigo-500/50'
              )
            }
          >
            {({ checked }) => (
              <>
                <div className='flex w-full items-center justify-between'>
                  <div className='mr-4 flex shrink-0 items-center justify-center'>
                    {user.avatarUrl ? (
                      <img
                        className='h-10 w-10 rounded-full'
                        src={user.avatarUrl} // Changed from avatar to avatarUrl
                        alt={user.displayName || 'User'} // Changed from name to displayName with fallback
                      />
                    ) : (
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${checked ? 'bg-sky-700' : 'bg-gray-200'}`}>
                        {(user.displayName || 'U').charAt(0)}
                      </div>
                    )}
                  </div>

                  <div className='flex grow items-center'>
                    <div className='text-sm'>
                      <RadioGroup.Label
                        as='p'
                        className={`font-medium ${checked ? 'text-white' : 'text-gray-900'}`}
                      >
                        {user.displayName || 'Unknown User'} {/* Changed from name to displayName with fallback */}
                      </RadioGroup.Label>
                      <RadioGroup.Description
                        as='span'
                        className={`inline ${checked ? 'text-sky-100' : 'text-gray-500'}`}
                      >
                        <span>{user.role || 'Team Member'}</span>
                      </RadioGroup.Description>
                    </div>
                  </div>

                  <div className='ml-2 flex shrink-0 items-center'>
                    {checked ? (
                      <CheckCircleIcon className='h-6 w-6 text-white' />
                    ) : (
                      <div className='h-6 w-6'></div> /* Empty div to maintain layout */
                    )}
                  </div>
                </div>
              </>
            )}
          </RadioGroup.Option>
        ))}
      </RadioGroup>
    </div>
  )
}

export default ProjectUserSelector
